import { Buffer } from 'buffer'
import { referenceDisplay } from '@seen/core/display'
import { invariant } from '@seen/core/invariant'
import { OrderLabel } from '@seen/core/models/orderLabel'
import {
  type OrderStatus,
  orderPriorityGroupLabel,
  PRIORITY_TIMEFRAME_OPTIONS,
} from '@seen/core/models/orders/constants'
import { Order } from '@seen/core/models/orders/Order'
import { sortOrdersByTier } from '@seen/core/models/orders/sorting'
import { OrderView } from '@seen/core/models/orderView'
import { RecentSelections } from '@seen/core/models/recentSelections'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { getReferenceString } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { Button, cn, IconStandingOrder } from '@seen/ui'
import {
  IconAlertCircle,
  IconBuilding,
  IconCalendar,
  IconCategory,
  IconChevronDown,
  IconId,
  IconPlus,
  IconProgress,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconUser,
} from '@tabler/icons-react'
import { isAfter } from 'date-fns'
import { chunk, isEmpty, isEqual, sortBy, uniqBy } from 'lodash-es'
import pluralize from 'pluralize'
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Spinner } from '#src/components/core/Loader.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { Sheet } from '#src/components/core/sheet/Sheet.tsx'
import { SheetContent } from '#src/components/core/sheet/SheetContent.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DisplayButton } from '#src/components/DisplayButton.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterEditor } from '#src/components/filter/FilterEditor.tsx'
import {
  FilterValue,
  useFilteredData,
} from '#src/components/filter/useFilteredData.ts'
import { Helmet } from '#src/components/Helmet.tsx'
import { IconLabel } from '#src/components/icons/IconLabel.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { ListHeader } from '#src/components/ListHeader.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import {
  defaultOrderStatusOptions,
  orderCategoryFilterOptions,
} from '#src/components/order/constants.ts'
import { OrderForm } from '#src/components/order/order-form/OrderForm.tsx'
import { OrderCreatedToast } from '#src/components/order/OrderCreatedToast.tsx'
import { OrderPage } from '#src/components/order/OrderPage.tsx'
import { OrderPriorityOption } from '#src/components/order/OrderPriorityCombobox.tsx'
import { OrderPriorityIcon } from '#src/components/order/OrderPriorityIcon.tsx'
import { OrdersList } from '#src/components/order/OrdersList.tsx'
import { useOrderAppointmentsMap } from '#src/components/order/useOrderAppointmentsMap.ts'
import { useOrderLastTouchedMap } from '#src/components/order/useOrderLastTouchedMap.ts'
import {
  DEFAULT_DISPLAY,
  useOrdersPageStore,
} from '#src/components/order/useOrdersPageStore.ts'
import { useLabelFilterOptions } from '#src/components/UseLabelFilterOptions.tsx'
import { ViewComposerDialogContent } from '#src/components/ViewComposerDialogContent.tsx'
import { ViewRow } from '#src/components/ViewRow.tsx'
import {
  defaultTokenize,
  useClientSideSearch,
} from '#src/hooks/useClientSideSearch.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebounced } from '#src/hooks/useDebounced.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { useOrders } from '#src/hooks/useOrders.ts'
import { useOrderSearchByIdentifier } from '#src/hooks/useOrderSearchByIdentifier.ts'
import { useOrganizationsFrom } from '#src/hooks/useOrganizationsFrom.ts'
import { useParticipantsFrom } from '#src/hooks/useParticipantsFrom.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePractitioners } from '#src/hooks/usePractitioners.ts'
import { usePractitionersFrom } from '#src/hooks/usePractitionersFrom.ts'
import { useQuery } from '#src/hooks/useQuery.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { toast } from '#src/utils/toast.ts'

const EMPTY_FILTERS: Filter[] = []

// Tokenizer that normalizes SR-identifier-shaped text into a canonical token
// plus its bare number, so `sr372`, `sr-372`, `SR-372`, and `372` all match
// order `SR-372`. Emitting both tokens lets MiniSearch's AND-combine keep the
// exact order on top: the canonical `SR-372` token is unique to that order,
// while fuzzy/prefix expansion (which alone would surface SR-352, SR-373, …)
// can't satisfy AND for any other order. Applied to the `identifier` field
// when indexing (fieldName === 'identifier') and to identifier-shaped queries
// (fieldName === undefined); everything else falls back to the default split.
// Scoped to orders only via the hook option.
function orderSearchTokenize(text: string, fieldName?: string): string[] {
  if (fieldName === 'identifier' || fieldName === undefined) {
    const normalized = Order.parseSeenIdentifier(text)
    if (normalized) {
      // Safe under processTerm (foldAndStem): it's field-agnostic and
      // deterministic, so the canonical `SR-372` token is stemmed identically
      // at index and query time. Symmetry — not the stemmer leaving it
      // untouched — is what guarantees the match.
      // `SR-372` -> ['SR-372', '372']
      return [normalized, normalized.slice('SR-'.length)]
    }
  }
  return defaultTokenize(text)
}

const ACTIVE_ORDERS_FILTERS: Filter[] = [
  {
    field: 'status',
    value: ['active', 'coordinated', 'effectuated'] satisfies OrderStatus[],
  },
]
const UNSIGNED_ORDERS_FILTERS: Filter[] = [
  { field: 'status', value: ['on-hold'] },
]

const ORDER_SORT_FIELDS = [
  {
    label: 'Hybrid (Priority + Status)',
    value: 'hybrid',
    keywords: ['hybrid', 'priority', 'status', 'tier', 'default'],
  },
  {
    label: 'Due date',
    value: 'dueDate',
    keywords: ['due', 'due date'],
  },
  {
    label: 'Next appointment date',
    value: 'nextAppointmentDate',
    keywords: ['next appointment', 'appointment', 'appointment date'],
  },
  {
    label: 'Priority',
    value: 'priority',
    keywords: ['priority', 'urgent', 'stat', 'routine'],
  },
  {
    label: 'Last updated',
    value: 'lastUpdated',
    keywords: ['last updated', 'recent', 'updated'],
  },
  {
    label: 'Created',
    value: 'date',
    keywords: ['created', 'order created'],
  },
  {
    label: 'Signed date',
    value: 'signedDate',
    keywords: ['signed', 'signed date', 'signature'],
  },
  {
    label: 'Participant name',
    value: 'patientName',
    keywords: ['patient', 'patient name', 'participant', 'ppt', 'name'],
  },
  {
    label: 'ID',
    value: 'id',
    keywords: ['id', 'identifier'],
  },
]
const DISPLAY_PROPERTIES: {
  key: keyof import('#src/components/order/useOrdersPageStore.ts').OrderViewDisplay
  label: string
}[] = [
  { key: 'showLabels', label: 'Labels' },
  { key: 'showNextAppointment', label: 'Next appointment' },
  { key: 'showNextTask', label: 'Next task' },
  { key: 'showLastNote', label: 'Last note' },
  { key: 'showRequester', label: 'Requester' },
  { key: 'showDueDate', label: 'Due date' },
  { key: 'showDocuments', label: 'Documents' },
]

const DEFAULT_SORT = { field: 'hybrid', direction: 'asc' as const }

const EMPTY_ORDERS: Order[] = []

export function OrdersPage(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isNewOrder = location.pathname.endsWith('/new')
  const viewedOrderId = searchParams.get('view')
  const selectedView = useOrdersPageStore((state) => state.view)
  const updateCurrentSort = useOrdersPageStore((s) => s.updateCurrentSort)
  const grouping = useOrdersPageStore((state) => state.grouping)
  const updateGrouping = useOrdersPageStore((state) => state.updateGrouping)
  const unsetGrouping = useOrdersPageStore((state) => state.unsetGrouping)
  const currentPractitioner = useCurrentPractitioner()
  const myOrderFilters = useMemo(
    () => [
      {
        field: 'requester',
        value: [currentPractitioner.reference.reference],
      },
      {
        field: 'status',
        value: [
          'active',
          'coordinated',
          'effectuated',
          'on-hold',
          'draft',
        ] satisfies OrderStatus[],
      },
    ],
    [currentPractitioner.reference.reference]
  )

  const { data: orderViews } = useSearch(
    OrderView,
    OrderView.queryForPractitioner(currentPractitioner.reference)
  )

  const { data: orderLabels } = useSearch(
    OrderLabel,
    OrderLabel.baseSearchQuery()
  )

  const sort = useOrdersPageStore((state) => state.sort)
  const filtersByView = useOrdersPageStore((state) => state.filtersByView)
  const storeFilters = filtersByView[selectedView] ?? EMPTY_FILTERS

  const { sortField, sortDirection, filters } = useMemo(() => {
    const q = searchParams.get('q')
    if (!q) {
      return {
        sortField: sort.field,
        sortDirection: sort.direction,
        filters: filtersByView[selectedView] ?? EMPTY_FILTERS,
      }
    }
    const { sortField, sortDirection, filters } = JSON.parse(
      Buffer.from(q, 'base64').toString('utf-8')
    )
    return { sortField, sortDirection, filters }
  }, [sort, filtersByView, searchParams, selectedView])

  // Derive opt-in statuses (completed/revoked) from the active status filter
  // so we only fetch them when the user explicitly filters by them.
  const optInStatuses = useMemo<OrderStatus[]>(() => {
    const statusFilter = (filters as Filter[]).find((f) => f.field === 'status')
    if (!statusFilter?.value?.length) {
      return []
    }
    return statusFilter.value.filter((v: string): v is OrderStatus =>
      (['completed', 'revoked'] as const).some((s) => s === v)
    )
  }, [filters])

  const { data = EMPTY_ORDERS, isLoading, error } = useOrders(optInStatuses)

  const hasSearchQueryParam = searchParams.get('q') !== null
  const sortChanged = useDidChange(sort.field + sort.direction)
  const filtersChanged = useDidChange(storeFilters)
  if (sortChanged || filtersChanged) {
    const q = Buffer.from(
      JSON.stringify({
        sortField: sort.field,
        sortDirection: sort.direction,
        filters: storeFilters,
      })
    ).toString('base64')
    searchParams.set('q', q)
    setSearchParams(searchParams)
  }

  const updateCurrentFilters = useOrdersPageStore(
    (state) => state.updateCurrentFilters
  )
  const unsetSort = useOrdersPageStore((state) => state.unsetSort)
  const changeView = useOrdersPageStore((state) => state.changeView)
  const lastViewedOrderId = useOrdersPageStore((s) => s.lastViewedOrderId)
  const displayByView = useOrdersPageStore((state) => state.displayByView)
  const setDisplay = useOrdersPageStore((state) => state.setDisplay)
  const unsetDisplay = useOrdersPageStore((state) => state.unsetDisplay)

  const baseDisplay = useMemo(() => DEFAULT_DISPLAY, [])

  const currentDisplay = useMemo(
    () => ({
      ...DEFAULT_DISPLAY,
      ...baseDisplay,
      ...displayByView[selectedView],
    }),
    [baseDisplay, displayByView, selectedView]
  )

  const isDisplayDirty = useMemo(
    () => !isEqual(currentDisplay, baseDisplay),
    [currentDisplay, baseDisplay]
  )

  const navigateFromClick = useNavigateFromClickEvent()
  const orderAppointmentsMap = useOrderAppointmentsMap(data)
  const hasLastTouchedFilter = filters.some(
    (f: Filter) => f.field === 'lastTouchedDate'
  )
  const { dateMap: orderLastTouchedMap, commentByOrder } =
    useOrderLastTouchedMap(data, hasLastTouchedFilter)

  /* Local input state decoupled from the URL so typing never blocks on
   * router updates or MiniSearch index rebuilds. The URL is driven off
   * the debounced value. Lazy initializer reads the URL param once on
   * mount; deep links and browser-history navigations work because React
   * Router mounts a fresh component instance on each navigation. */
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get('search') ?? ''
  )
  const debouncedSearch = useDebounced(searchInput, 300)

  /* Push the debounced input value to the URL. Skips when already in sync
   * to avoid unnecessary history writes. Preserves all other params (e.g.
   * q, view). */
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const current = prev.get('search') ?? ''
        if (current === debouncedSearch) {
          return prev
        }
        if (debouncedSearch) {
          prev.set('search', debouncedSearch)
        } else {
          prev.delete('search')
        }
        return prev
      },
      { replace: true }
    )
  }, [debouncedSearch, setSearchParams])

  const [composerOpen, setComposerOpen] = useState(false)
  const [editView, setEditView] = useState<OrderView | undefined>(undefined)

  const { data: recentSelections } = useRead(
    RecentSelections,
    currentPractitioner.recentSelections
  )
  const patchRecentSelections = usePatchMutation<RecentSelections>(
    currentPractitioner.recentSelections?.reference
      ? {
          mutation: {
            scope: {
              id: currentPractitioner.recentSelections.reference,
            },
          },
        }
      : undefined
  ).mutate

  const viewFilters = useMemo(() => {
    if (selectedView === 'all') {
      return []
    }
    if (selectedView === 'active') {
      return ACTIVE_ORDERS_FILTERS
    }
    if (selectedView === 'unsigned') {
      return UNSIGNED_ORDERS_FILTERS
    }
    if (selectedView === 'myOrders') {
      return myOrderFilters
    }
    const view = orderViews?.find((v) => v.referenceString === selectedView)
    return view ? JSON.parse((view as OrderView).filters) : EMPTY_FILTERS
  }, [orderViews, selectedView, myOrderFilters])

  const didViewFiltersChange = useDidChange(viewFilters)
  if (
    !hasSearchQueryParam &&
    didViewFiltersChange &&
    !isEmpty(viewFilters) &&
    isEmpty(filters)
  ) {
    updateCurrentFilters(viewFilters)
  }

  const isFiltersDirty = useMemo(
    () => !isEqual(filters, viewFilters),
    [filters, viewFilters]
  )
  const isSortDirty = useMemo(
    () =>
      !isEqual({ field: sortField, direction: sortDirection }, DEFAULT_SORT),
    [sortField, sortDirection]
  )

  const isGroupingDirty = useMemo(() => grouping !== undefined, [grouping])

  const orderParticipants = useMemo(
    () => data?.map((o) => o.participant).filter((p) => p !== undefined) ?? [],
    [data]
  )
  const participants = useParticipantsFrom(orderParticipants)
  const participantOptions = useMemo(
    () =>
      participants.map((p) => ({
        label: <ParticipantOption participant={p.reference} />,
        value: p.reference.reference,
        icon: <Avatar profile={p.reference} className="size-4" />,
        keywords: p.searchKeywords,
      })),
    [participants]
  )

  const idOptions = useMemo(
    () =>
      uniqBy(
        data
          ?.filter((order) => order.seenIdentifier)
          .map((order) => ({
            label: order.seenIdentifier ?? '',
            value: order.seenIdentifier ?? '',
          })) ?? [],
        'value'
      ),
    [data]
  )

  const practitioners = usePractitioners()
  const practitionerOptions = useMemo(
    () =>
      practitioners.map((p) => ({
        label: <PractitionerOption practitioner={p.reference} />,
        value: p.reference.reference,
        icon: <Avatar profile={p.reference} size="icon" className="size-4" />,
        keywords: p.searchKeywords,
      })),
    [practitioners]
  )

  const orderPerformers = useMemo(
    () => data?.map((o) => o.performer).filter((a) => a !== undefined) ?? [],
    [data]
  )
  const practitionerOrderPerformers = useMemo(
    () => orderPerformers.filter((p) => isOfType(p, 'Practitioner')),
    [orderPerformers]
  )
  const organizationOrderPerformers = useMemo(
    () => orderPerformers.filter((p) => isOfType(p, 'Organization')),
    [orderPerformers]
  )
  const organizationPerformers = useOrganizationsFrom(
    organizationOrderPerformers
  )
  const practitionerPerformers = usePractitionersFrom(
    practitionerOrderPerformers
  )
  const performerOptions = useMemo(
    () => [
      ...practitionerPerformers.map((p) => ({
        label: <PractitionerOption practitioner={p.reference} />,
        value: p.reference.reference,
        icon: <Avatar profile={p.reference} size="icon" className="size-4" />,
        keywords: p.searchKeywords,
      })),
      ...organizationPerformers.map((o) => ({
        label: o.name,
        value: o.reference.reference,
        icon: <IconBuilding className="size-4" />,
        keywords: o.searchKeywords,
      })),
    ],
    [practitionerPerformers, organizationPerformers]
  )

  const nextTasksByOrder = useOrdersNextIncompleteTaskMap(data ?? [])

  const nextTaskOwners = useMemo(() => {
    if (!nextTasksByOrder) {
      return []
    }
    const owners = Array.from(nextTasksByOrder.values())
      .map((task) => task.ownerPractitioner)
      .filter(
        (owner): owner is NonNullable<typeof owner> => owner !== undefined
      )
    return uniqBy(owners, 'reference')
  }, [nextTasksByOrder])

  const nextTaskOwnerPractitioners = usePractitionersFrom(nextTaskOwners)
  const nextTaskOwnerOptions = useMemo(
    () => [
      {
        label: (
          <div className="flex items-center gap-2">
            <IconUserPlaceholder className="size-5 p-0" /> No owner
          </div>
        ),
        selectedLabel: (
          <div className="flex items-center gap-1">
            <IconUserPlaceholder className="size-3.5 p-0" /> No owner
          </div>
        ),
        value: FilterValue.None,
        icon: <IconUserPlaceholder className="size-3.5 p-0" />,
        keywords: ['No owner'],
      },
      ...nextTaskOwnerPractitioners.map((p) => ({
        label: <PractitionerOption practitioner={p.reference} />,
        value: p.reference.reference,
        icon: <Avatar profile={p.reference} size="icon" className="size-4" />,
        keywords: p.searchKeywords,
      })),
    ],
    [nextTaskOwnerPractitioners]
  )

  const labelOptions = useLabelFilterOptions(orderLabels)

  const filterOptions = useMemo<FilterConfigMap<Order>>(
    () =>
      ({
        status: {
          label: 'Status',
          icon: <IconProgress className="size-4" />,
          type: 'multi-select',
          options: defaultOrderStatusOptions.map((o) => ({
            ...o,
            keywords: [o.label],
            icon: <IconOrderStatus status={o.value} className="size-4" />,
            label: (
              <div className="flex items-center gap-2">
                <IconOrderStatus status={o.value} className="size-4" />
                {o.label}
              </div>
            ),
          })),
          valueFn: (o) => o.status,
        },

        labels: {
          label: 'Labels',
          icon: <IconLabel className="size-4" />,
          type: 'multi-select-array',
          options: labelOptions,
          valueFn: (o) => o.labels,
        },

        participant: {
          label: 'Participant',
          icon: <IconUser className="size-4" />,
          type: 'multi-select',
          options: participantOptions,
          valueFn: (o) => o.participant?.reference ?? '',
        },

        associatedWith: {
          label: 'Associated with',
          icon: <IconUser className="size-4" />,
          type: 'multi-select-array',
          options: practitionerOptions,
          valueFn: (o) => o.associatedPractitioners.map(getReferenceString),
        },

        id: {
          label: 'ID',
          icon: <IconId className="size-4" />,
          type: 'multi-select',
          options: idOptions.map((o) => ({
            ...o,
            keywords: [o.label],
          })),
          valueFn: (o) => o.seenIdentifier ?? '',
        },

        category: {
          label: 'Category',
          icon: <IconCategory className="size-4" />,
          type: 'multi-select',
          options: orderCategoryFilterOptions.map((o) => ({
            ...o,
            keywords: [o.label],
          })),
          valueFn: (o) => o.category,
        },

        requester: {
          label: 'Requester',
          icon: <IconUser className="size-4" />,
          type: 'multi-select',
          options: practitionerOptions,
          valueFn: (o) => o.requester?.reference ?? '',
        },

        assignee: {
          label: 'Rendering Provider / Vendor',
          icon: <IconUser className="size-4" />,
          type: 'multi-select',
          options: performerOptions,
          valueFn: (o) => o.performer?.reference ?? '',
        },

        detail: {
          label: 'Detail',
          icon: <IconSearch className="size-4" />,
          type: 'text',
          valueFn: (o) => o.searchKeywords.join(' '),
        },

        date: {
          label: 'Date',
          icon: <IconCalendar className="size-4" />,
          type: 'date',
          valueFn: (o) => o.date?.toISOString() ?? '',
        },

        updated: {
          label: 'Updated',
          icon: <IconCalendar className="size-4" />,
          type: 'date',
          valueFn: (o) => o.lastUpdated ?? '',
        },

        dueDate: {
          label: 'Due Date',
          icon: <IconCalendar className="size-4" />,
          type: 'date',
          valueFn: (o) => o.dueDate ?? '',
        },

        signedDate: {
          label: 'Signed Date',
          icon: <IconCalendar className="size-4" />,
          type: 'date',
          valueFn: (o) => o.signedDate ?? '',
        },

        nextAppointmentDate: {
          label: 'Next Appointment Date',
          icon: <IconCalendar className="size-4" />,
          type: 'date',
          valueFn: (o) => {
            const nextAppointment = orderAppointmentsMap.get(
              o.reference.reference
            )
            return nextAppointment?.start ?? ''
          },
        },

        lastTouchedDate: {
          label: 'Last Touched',
          icon: <IconCalendar className="size-4" />,
          type: 'date',
          /* '' for orders with no manual notes — parseISO('') is invalid,
           * so they're excluded from date operators (before/after/equals/between)
           * but included by the "Missing" (FilterValue.None) option */
          valueFn: (o) => orderLastTouchedMap.get(o.reference.reference) ?? '',
        },

        overdue: {
          label: 'Overdue',
          icon: <IconAlertCircle className="size-4" />,
          type: 'boolean',
          valueFn: (o) => o.isOverdue,
        },

        priority: {
          label: 'Appointment Timing',
          icon: <OrderPriorityIcon priority="asap-14" className="size-4" />,
          type: 'multi-select',
          options: [
            ...PRIORITY_TIMEFRAME_OPTIONS.map((opt) => {
              const groupLabel = orderPriorityGroupLabel(opt.value)
              return {
                value: opt.value,
                label: (
                  <OrderPriorityOption
                    priority={opt.value}
                    isSelected={false}
                  />
                ),
                selectedLabel: `${groupLabel}: ${opt.label}`,
                icon: (
                  <OrderPriorityIcon priority={opt.value} className="size-4" />
                ),
                keywords: [opt.label, opt.priority, groupLabel],
              }
            }),
            {
              value: FilterValue.None,
              label: (
                <div className="flex items-center gap-2">
                  <OrderPriorityIcon priority={null} className="size-4" />
                  No Priority
                </div>
              ),
              icon: <OrderPriorityIcon priority={null} className="size-4" />,
              keywords: ['No Priority', 'none', 'null'],
            },
          ],
          valueFn: (o) => o.priority ?? FilterValue.None,
        },

        standing: {
          label: 'Standing',
          icon: <IconStandingOrder className="size-4" />,
          type: 'boolean',
          valueFn: (o) => o.standing,
        },

        nextTaskOwner: {
          label: 'Next Task Owner',
          icon: <IconUser className="size-4" />,
          type: 'multi-select',
          options: nextTaskOwnerOptions,
          valueFn: (o) => {
            const nextTask = nextTasksByOrder?.get(o.reference.reference)
            return nextTask?.ownerPractitioner?.reference ?? ''
          },
        },
      }) as const,
    [
      orderAppointmentsMap,
      orderLastTouchedMap,
      participantOptions,
      idOptions,
      performerOptions,
      nextTaskOwnerOptions,
      nextTasksByOrder,
      practitionerOptions,
      labelOptions,
    ]
  )

  // The default order list excludes completed/revoked orders for performance,
  // so searching a completed order's SR identifier wouldn't surface it. When
  // the query parses as an SR identifier or bare order number, look it up on
  // demand and merge it in (deduped) so it flows through the same
  // filter/search/sort pipeline. The hook no-ops for non-identifier input.
  const { data: identifierOrders } = useOrderSearchByIdentifier(
    debouncedSearch,
    { matchBareNumber: true }
  )
  const dataWithIdentifier = useMemo(() => {
    if (!identifierOrders?.length) {
      return data
    }
    return uniqBy([...data, ...identifierOrders], (o) => o.reference.reference)
  }, [data, identifierOrders])

  const filteredDataRaw = useFilteredData(
    dataWithIdentifier,
    filterOptions,
    filters
  )

  const extractOrderSearchFields = useCallback(
    (order: Order) => ({
      title: order.title ?? '',
      aiTitle: order.aiTitle ?? '',
      category: order.categoryCodeDisplay ?? '',
      details: order.orderDetailsText ?? '',
      reasons: order.reasonTexts.join(' '),
      specialty: order.specialty?.display ?? '',
      performer: referenceDisplay(order.performer) ?? '',
      requester: referenceDisplay(order.requester) ?? '',
      identifier: order.seenIdentifier ?? '',
      authorizationId: order.authorizationId ?? '',
      // Line-item test names (e.g. "Complete Metabolic Panel") from SR.code.coding[]
      codeDisplays: order.displayCodes
        .map((c) => c.display)
        .filter(Boolean)
        .join(' '),
      // Patient name — already loaded on the order for the patientName sort column
      participant: referenceDisplay(order.participant) ?? '',
    }),
    []
  )

  const { search: searchOrders, getExtractedFields } = useClientSideSearch(
    filteredDataRaw,
    {
      fields: [
        'title',
        'aiTitle',
        'category',
        'details',
        'reasons',
        'specialty',
        'performer',
        'requester',
        'identifier',
        'authorizationId',
        'codeDisplays',
        'participant',
      ],
      boost: {
        title: 5,
        aiTitle: 5,
        identifier: 4,
        authorizationId: 3,
        codeDisplays: 3,
        category: 2,
        performer: 2,
        requester: 2,
        specialty: 2,
        participant: 2,
        details: 1,
        reasons: 1,
      },
      extractFields: extractOrderSearchFields,
      idleBuild: true,
      tokenize: orderSearchTokenize,
    }
  )

  const { items: searchFilteredData, matchById } = useMemo(
    () => searchOrders(debouncedSearch),
    [searchOrders, debouncedSearch]
  )

  const filteredData = useMemo((): Order[] => {
    if (sortField === 'hybrid') {
      const sorted = sortOrdersByTier(searchFilteredData)
      return sortDirection === 'desc' ? sorted.slice().reverse() : sorted
    }
    const sorted = sortBy(searchFilteredData, [
      (order: Order) => {
        if (sortField === 'lastUpdated') {
          return order.lastUpdated
        }
        if (sortField === 'date') {
          return order.date ? order.date.toISOString() : ''
        }
        if (sortField === 'dueDate') {
          return order.dueDate ?? ''
        }
        if (sortField === 'signedDate') {
          return order.signedDate ? order.signedDate : ''
        }
        if (sortField === 'nextAppointmentDate') {
          const nextAppointment = orderAppointmentsMap.get(
            order.reference.reference
          )
          return nextAppointment?.start ?? ''
        }
        if (sortField === 'id') {
          /* Prefer numeric sort by SR-xxx identifier if present */
          const match = order.seenIdentifier?.match(/^SR-(\d+)$/)
          if (match) {
            return parseInt(match[1], 10)
          }
          return order.seenIdentifier
        }
        if (sortField === 'patientName') {
          return referenceDisplay(order.participant)?.toLowerCase() ?? ''
        }
        if (sortField === 'priority') {
          /* Map priority to numeric value for sorting */
          /* stat-1 = 1, urgent-7 = 2, asap-14 = 3, routine-* = 4, null = 5 */
          const priority = order.priority
          if (!priority) {
            return 5
          }
          if (priority.startsWith('stat')) {
            return 1
          }
          if (priority.startsWith('urgent')) {
            return 2
          }
          if (priority.startsWith('asap')) {
            return 3
          }
          if (priority.startsWith('routine')) {
            return 4
          }
          /* Legacy/unknown values default to low priority */
          return 4
        }
        return ''
      },
    ])
    return sortDirection === 'desc' ? sorted.slice().reverse() : sorted
  }, [searchFilteredData, sortField, sortDirection, orderAppointmentsMap])

  return (
    <div className={cn('relative flex flex-col', 'size-full print:size-auto')}>
      <Helmet title="Orders" />
      <ListHeader>
        <Button
          variant="outline"
          size="xs"
          selected={selectedView === 'all'}
          onClick={() => {
            changeView('all', [])
            updateCurrentFilters([])
          }}
        >
          All orders
        </Button>
        <Button
          variant="outline"
          size="xs"
          selected={selectedView === 'myOrders'}
          onClick={() => {
            changeView('myOrders', myOrderFilters)
            updateCurrentFilters(myOrderFilters)
          }}
        >
          My orders
        </Button>
        <Button
          variant="outline"
          size="xs"
          selected={selectedView === 'active'}
          onClick={() => {
            changeView('active', ACTIVE_ORDERS_FILTERS)
            updateCurrentFilters(ACTIVE_ORDERS_FILTERS)
          }}
        >
          Active
        </Button>
        <Button
          variant="outline"
          size="xs"
          selected={selectedView === 'unsigned'}
          onClick={() => {
            changeView('unsigned', UNSIGNED_ORDERS_FILTERS)
            updateCurrentFilters(UNSIGNED_ORDERS_FILTERS)
          }}
        >
          Unsigned
        </Button>
        <ViewRow
          views={orderViews}
          selectedView={selectedView}
          onSelectView={(newViewReferenceString) => {
            const newView = orderViews?.find(
              (v) => v.referenceString === newViewReferenceString
            )
            invariant(newView, 'Order view not found')
            const filters = JSON.parse(newView.filters)
            changeView(newViewReferenceString, filters)
            updateCurrentFilters(filters)
          }}
          onDeleteSelectedView={() => {
            changeView('myOrders', myOrderFilters)
            updateCurrentFilters(myOrderFilters)
          }}
          className="flex-1"
        />
      </ListHeader>
      <div className="flex items-center justify-between gap-2 border-b-[0.5px] px-pagex-default py-2 lg:px-pagex-lg">
        <div className="flex items-center gap-2">
          <FilterEditor
            filterOptions={filterOptions}
            filters={filters}
            setFilters={updateCurrentFilters}
          />
          <Input
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-6 w-48 shrink-0 shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <div className="flex items-center gap-1 text-2xs whitespace-nowrap text-foreground-secondary">
            {filteredData.length} {pluralize('order', filteredData.length)}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">*</span>
              </TooltipTrigger>
              <TooltipContent>
                Completed and revoked orders are excluded by default. Filter by
                those statuses to include them.
              </TooltipContent>
            </Tooltip>
            {isLoading && <Spinner className="size-3.5" />}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <DisplayButton
                    showIndicator={
                      isDisplayDirty || isSortDirty || isGroupingDirty
                    }
                  />
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent>Display options</TooltipContent>
            </Tooltip>
            <PopoverContent className="w-[292px] p-0 py-2">
              {/* Offscreen focus trap prevents tooltips inside from auto-triggering on popover open */}
              <span
                // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- intentional focus trap
                tabIndex={0}
                style={{
                  position: 'absolute',
                  top: 9999,
                  right: 9999,
                  width: 0,
                  height: 0,
                }}
              />
              <div className="flex flex-col gap-2">
                <div className="flex flex-col px-2">
                  <div className="flex items-center gap-4 px-4 py-1">
                    <span className="w-16 shrink-0 text-sm font-medium">
                      Sorting
                    </span>
                    <div className="flex min-w-0 flex-1 gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="size-7 shrink-0"
                            onClick={() =>
                              updateCurrentSort((prev) => ({
                                ...prev,
                                direction:
                                  prev.direction === 'asc' ? 'desc' : 'asc',
                              }))
                            }
                          >
                            {sortDirection === 'asc' ? (
                              <IconSortAscending className="size-4" />
                            ) : (
                              <IconSortDescending className="size-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                        </TooltipContent>
                      </Tooltip>
                      <Combobox
                        options={ORDER_SORT_FIELDS}
                        onSelect={(field) =>
                          updateCurrentSort((prev) => ({
                            field,
                            direction: prev.direction,
                          }))
                        }
                      >
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5"
                        >
                          <span className="flex-1 truncate text-left text-xs">
                            {ORDER_SORT_FIELDS.find(
                              (f) => f.value === sortField
                            )?.label ?? 'Field'}
                          </span>
                          <IconChevronDown className="size-4 shrink-0 text-muted-foreground" />
                        </button>
                      </Combobox>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 px-4 py-1">
                    <span className="w-16 shrink-0 text-sm font-medium">
                      Grouping
                    </span>
                    <div className="flex min-w-0 flex-1 gap-1">
                      <Combobox
                        options={[
                          {
                            label: (
                              <ComboboxOption
                                isSelected={grouping === undefined}
                              >
                                None
                              </ComboboxOption>
                            ),
                            value: null,
                            keywords: ['None', 'No grouping'],
                          },
                          {
                            label: (
                              <ComboboxOption
                                isSelected={grouping === 'status'}
                              >
                                Status
                              </ComboboxOption>
                            ),
                            value: 'status',
                            keywords: ['Status', 'Group by status'],
                          },
                          {
                            label: (
                              <ComboboxOption
                                isSelected={grouping === 'performer'}
                              >
                                Performer
                              </ComboboxOption>
                            ),
                            value: 'performer',
                            keywords: ['Performer', 'Group by performer'],
                          },
                        ]}
                        onSelect={(value) => updateGrouping(value ?? undefined)}
                      >
                        <button
                          type="button"
                          className="flex w-full items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5"
                        >
                          <span className="flex-1 truncate text-left text-xs">
                            {grouping === 'status' && 'Status'}
                            {grouping === 'performer' && 'Performer'}
                            {!grouping && 'None'}
                          </span>
                          <IconChevronDown className="size-4 shrink-0 text-muted-foreground" />
                        </button>
                      </Combobox>
                    </div>
                  </div>
                </div>
                <div className="h-px bg-border" />
                <div className="flex flex-col px-2">
                  <div className="px-1.5 pt-2">
                    <span className="text-[13px] leading-[18px] font-bold tracking-[0.52px] text-muted-foreground uppercase">
                      Display properties
                    </span>
                  </div>
                  {DISPLAY_PROPERTIES.map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 px-4 py-2"
                    >
                      <Checkbox
                        value={currentDisplay[key]}
                        onChange={() =>
                          setDisplay({
                            display: (d) => ({
                              ...d,
                              [key]: !currentDisplay[key],
                            }),
                            forView: selectedView,
                          })
                        }
                      />
                      <span className="text-sm font-medium">{label}</span>
                    </label>
                  ))}
                </div>
                {(isDisplayDirty || isSortDirty || isGroupingDirty) && (
                  <div className="flex justify-end border-t px-2 pt-2">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        unsetDisplay(selectedView)
                        unsetSort()
                        unsetGrouping()
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          <div className="h-4 w-[0.5px] bg-border" />
          <Button
            variant="ghost"
            size="xs"
            onClick={() => {
              updateCurrentFilters(viewFilters)
            }}
            disabled={!isFiltersDirty}
          >
            Reset
          </Button>
          <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
            <DialogButton
              variant="outline"
              size="xs"
              onClick={() => setEditView(undefined)}
              disabled={!isFiltersDirty}
            >
              Save
            </DialogButton>
            <DialogDisplay>
              <OrderViewDialogContent
                filters={filters}
                onSubmit={(view) => {
                  setComposerOpen(false)
                  changeView(view.referenceString, JSON.parse(view.filters))
                  if (recentSelections) {
                    patchRecentSelections({
                      instance: recentSelections,
                      mutateFn: (rs) => {
                        rs.add(view.reference.reference)
                      },
                    })
                  }
                }}
                view={editView}
              />
            </DialogDisplay>
          </Dialog>
          {!isReadOnly && (
            <>
              <div className="h-4 w-[0.5px] bg-border" />
              <Button
                variant="outline"
                size="xs"
                onClick={(event) => navigateFromClick('/orders/new', event)}
                className="flex items-center gap-2"
              >
                <IconPlus size={16} />
                New Order
              </Button>
            </>
          )}
        </div>
      </div>
      {!isLoading &&
        isEmpty(filteredData) &&
        !isEmpty(filteredDataRaw) &&
        debouncedSearch && (
          <p className="text-muted-foreground">
            No orders matched your search.
          </p>
        )}
      <div className="flex-1 overflow-hidden">
        <OrdersList
          data={filteredData}
          isLoading={isLoading}
          error={error}
          lastViewedOrderId={lastViewedOrderId}
          display={currentDisplay}
          grouping={grouping}
          commentByOrder={commentByOrder}
          matchById={debouncedSearch ? matchById : undefined}
          getExtractedFields={debouncedSearch ? getExtractedFields : undefined}
        />
      </div>
      <Sheet
        open={isNewOrder}
        onOpenChange={(open) => !open && navigate('..', { replace: true })}
      >
        <SheetContent hideClose side="right" className="max-w-2xl">
          <OrderForm
            onCancel={() => navigate('..')}
            onSubmit={(newOrder, options) => {
              void navigate('..')
              // Skip the generic "Order created" toast for deferred (async)
              // submissions — LabSubmissionWatcher surfaces the terminal
              // toast once the subscription handler completes.
              if (!options?.deferred) {
                toast.success(<OrderCreatedToast order={newOrder} />)
              }
            }}
          />
        </SheetContent>
      </Sheet>
      <Sheet
        open={!!viewedOrderId && !isNewOrder}
        onOpenChange={(open) => {
          if (!open) {
            searchParams.delete('view')
            setSearchParams(searchParams)
          }
        }}
      >
        <SheetContent hideClose side="right" className="max-w-3xl">
          {viewedOrderId && <OrderPage orderId={viewedOrderId} />}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function useOrdersNextIncompleteTaskMap(
  orders: Order[]
): Map<string, StandardTask> | undefined {
  const medplum = useMedplum()
  const orderChunks = useMemo(() => chunk(orders, 100), [orders])

  const { data: taskOrderMap } = useQuery({
    queryKey: ['task-order-map', orders],
    queryFn: async () => {
      const tasks = await Promise.all(
        orderChunks.map((chunk) =>
          medplum.searchResourceClass(StandardTask, {
            ...StandardTask.byFocuses(chunk.map((order) => order.reference)),
            'status:not': 'completed,cancelled,entered-in-error',
            _count: 1000,
          })
        )
      ).then((results) => results.flat())
      return tasks.reduce((acc, task) => {
        const reference = task.focus && getReferenceString(task.focus)
        if (!reference) {
          return acc
        }

        const existing = acc.get(reference)
        if (!existing) {
          acc.set(reference, task)
          return acc
        }

        const existingDueOn = existing.dueOn
        const taskDueOn = task.dueOn
        if (!existingDueOn || !taskDueOn) {
          if (!existingDueOn && taskDueOn) {
            acc.set(reference, task)
          }
          return acc
        }

        if (isAfter(existingDueOn, taskDueOn)) {
          acc.set(reference, task)
        }

        return acc
      }, new Map<string, StandardTask>())
    },
  })

  return taskOrderMap
}

function OrderViewDialogContent({
  filters,
  onSubmit,
  view,
}: {
  filters: Filter[]
  onSubmit: (view: OrderView) => void
  view?: OrderView
}): JSX.Element {
  const createMutation = useCreateMutation<OrderView>()
  const currentPractitioner = useCurrentPractitioner()

  return (
    <ViewComposerDialogContent
      isSubmitting={createMutation.isPending}
      view={view}
      onSubmit={(values) =>
        createMutation.mutate(
          new OrderView({
            name: values.name,
            filters: JSON.stringify(filters),
            owner: currentPractitioner.reference,
            access: values.access,
          }),
          { onSuccess: onSubmit }
        )
      }
    />
  )
}
